// ** React Imports
import { useState, SyntheticEvent, Fragment, ReactNode } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { styled, Theme, type SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiMenu, { MenuProps } from '@mui/material/Menu'
import MuiMenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import Typography, { TypographyProps } from '@mui/material/Typography'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'

// ** Type Imports
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import CustomChip from 'src/@core/components/mui/chip'
import { AppDispatch } from '@/src/store'
import { useDispatch } from 'react-redux'
import { markAllRead } from '@/src/store/apps/resto365Notifications'
import { Drawer, Paper, PaperProps, Stack } from '@mui/material'
import GygButton from '@/src/gygComponents/gygButton'

export type NotificationsType = {
  meta: string
  title: string
  subtitle: string
  read?: boolean
}

/** Optional trigger styling for `sidebar` mode (e.g. BHYBE light rail — icon-only, dark icon). */
export type NotificationSidebarTriggerProps = {
  iconOnly?: boolean
  bellIcon?: string
  bellIconColor?: string
  iconButtonSx?: SxProps<Theme>
}

interface Props {
  settings: Settings
  notifications: NotificationsType[]
  isSocketConnected: boolean
  sidebar?: boolean
  sidebarTrigger?: NotificationSidebarTriggerProps
}

// ** Styled Menu component
const Menu = styled(MuiMenu)<MenuProps>(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 500,
    overflow: 'hidden',
    marginTop: theme.spacing(4.25),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0,
    '& .MuiMenuItem-root': {
      margin: 0,
      borderRadius: 0,
      padding: theme.spacing(4, 6),
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }
  }
}))

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: 349
})

// ** Styled component for the title in MenuItems
const MenuItemTitle = styled(Typography)<TypographyProps>({
  fontWeight: 500,
  flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
})

// ** Styled component for the subtitle in MenuItems
const MenuItemSubtitle = styled(Typography)<TypographyProps>({
  flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
})

const Section = styled(Paper)<PaperProps>(() => ({
  square: false,
  borderRadius: '20px',
  padding: '20px'
}))

const ScrollWrapper = ({ children, hidden }: { children: ReactNode; hidden: boolean }) => {
  if (hidden) {
    return <Box sx={{ maxHeight: 349, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
  } else {
    return <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
  }
}

const OFFLINE_LABEL = 'X'

const NotificationDropdown = (props: Props) => {
  // ** Props
  const { settings, notifications, isSocketConnected, sidebar = false, sidebarTrigger } = props
  const dispatch = useDispatch<AppDispatch>()

  // ** States
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null)

  // ** Hook
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  // ** Vars
  const { direction, navCollapsed } = settings

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
    dispatch(markAllRead())
  }

  const unreadCount = () => {
    return notifications.filter(n => !n.read).length
  }

  return (
    <Fragment>
      {sidebar ? (
        <Fragment>
          <Box
            sx={{
              userSelect: 'none',
              cursor: 'pointer',

              /** Icon-only rail triggers must shrink-wrap so a footer row can fit bell + other icons */
              width: sidebarTrigger?.iconOnly ? 'auto' : '100%',
              display: 'flex',
              justifyContent: 'center',
              flexShrink: sidebarTrigger?.iconOnly ? 0 : undefined
            }}
          >
            {sidebarTrigger?.iconOnly ? (
              <Tooltip title='Notifications'>
                <IconButton aria-label='Notifications' onClick={handleDropdownOpen} sx={sidebarTrigger.iconButtonSx}>
                  <Badge
                    color='error'
                    badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
                    sx={{
                      '& .MuiBadge-badge': {
                        boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                      }
                    }}
                  >
                    <Icon
                      fontSize={24}
                      icon={sidebarTrigger.bellIcon ?? 'tabler:bell'}
                      color={sidebarTrigger.bellIconColor ?? '#fff'}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={handleDropdownOpen}>
                <Stack direction={'row'} alignItems={'center'} className='testiongsdfsd'>
                  <Icon fontSize={24} icon='tabler:bell' color='#fff' />
                  {!navCollapsed && (
                    <Typography variant='body1' sx={{ color: '#fff', paddingLeft: 2 }}>
                      Notifications
                    </Typography>
                  )}
                  <Badge
                    color='error'
                    badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
                    sx={{
                      '& .MuiBadge-badge': {
                        boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                      }
                    }}
                  ></Badge>
                </Stack>
              </IconButton>
            )}
          </Box>
          <Drawer className='gyg-drawer' open={Boolean(anchorEl)} anchor='right'>
            <Stack
              direction={'column'}
              sx={{
                '& .MuiCard-root': {
                  boxShadow: 'none'
                }
              }}
            >
              <Stack
                height={72}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                position={'sticky'}
                zIndex={1}
                sx={{ padding: '32px 24px 8px 24px' }}
              >
                <Stack direction={'row'} gap={5} alignItems={'center'}>
                  <Typography variant='h1'>Notifications</Typography>
                  <Box sx={{ backgroundColor: '#fbefc7', padding: '6px 10px', borderRadius: '4px' }}>
                    <Typography variant='body1' fontWeight={400} fontSize={16}>{`${unreadCount()} new`}</Typography>
                  </Box>
                </Stack>
                <IconButton aria-label='close' onClick={() => setAnchorEl(null)} sx={{ color: '#000' }}>
                  <Icon icon='ic:baseline-close' fontSize={32} />
                </IconButton>
              </Stack>

              <Stack height={'calc(100vh - 244px)'} overflow={'auto'} direction={'column'} padding={'24px 24px 0 24px'}>
                {notifications.map((notification: NotificationsType, index: number) => (
                  <Section sx={{ boxShadow: 'unset', borderRadius: '16px', padding: '15px', marginY: 2 }} key={index}>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                      <Stack
                        sx={{
                          mr: 4,
                          ml: 2.5,
                          flex: '1 1',
                          display: 'flex',
                          overflow: 'hidden',
                          flexDirection: 'column'
                        }}
                      >
                        <Typography variant='body1'>{notification.title}</Typography>
                        <Typography variant='body2'>{notification.subtitle}</Typography>
                      </Stack>
                      <Typography variant='body2'>{notification.meta}</Typography>
                    </Box>
                  </Section>
                ))}
              </Stack>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                position={'sticky'}
                height={100}
                bottom={0}
                zIndex={1}
                gap={3}
                sx={{
                  padding: '24px 32px 32px 32px',
                  boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'rgba(255, 255, 255, 1)'
                }}
              >
                <GygButton
                  color='secondary'
                  size='large'
                  text={'Close'}
                  variant='contained'
                  onClick={handleDropdownClose}
                />
              </Stack>
            </Stack>
          </Drawer>
        </Fragment>
      ) : (
        <>
          <IconButton color='inherit' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
            <Badge
              color='error'
              badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
              sx={{
                '& .MuiBadge-badge': {
                  boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                }
              }}
            >
              <Icon fontSize={24} icon='tabler:bell' color='#fff' />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleDropdownClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant='h5' sx={{ cursor: 'text' }}>
                  Notifications
                </Typography>
                <CustomChip skin='light' size='small' color='primary' label={`${unreadCount()} New`} />
              </Box>
            </MenuItem>
            <ScrollWrapper hidden={hidden}>
              {notifications.map((notification: NotificationsType, index: number) => (
                <MenuItem key={index} disableRipple disableTouchRipple onClick={handleDropdownClose}>
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{ mr: 4, ml: 2.5, flex: '1 1', display: 'flex', overflow: 'hidden', flexDirection: 'column' }}
                    >
                      <MenuItemTitle>{notification.title}</MenuItemTitle>
                      <MenuItemSubtitle variant='body2'>{notification.subtitle}</MenuItemSubtitle>
                    </Box>
                    <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                      {notification.meta}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </ScrollWrapper>
            {/*  */}
          </Menu>
        </>
      )}
    </Fragment>
  )
}

export default NotificationDropdown
